// TODO: Refactor PDP into separate hooks so that react-hooks/rules-of-hooks can be enabled
/* eslint-disable react-hooks/rules-of-hooks */

import React, {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import Helmet from 'react-helmet';
import { hot } from 'react-hot-loader/root';
import { forceVisible } from 'react-lazyload';
import { Redirect, useLocation, useParams } from 'react-router-dom';
import { isEmpty } from 'lodash';

import Button from '../../library/button/button';
import Checkbox from '../../library/checkbox/checkbox';
import Link from '../../library/link/link';
import Page, { Content, Section } from '../../library/page/page';
import StarRating, { RatingLink } from '../../library/star-rating/star-rating';
import Tabs, { Tab } from '../../library/tabs/tabs';
import Typography from '../../library/typography/typography';
import BackButton from '../../components/back-button/back-button';
import Breadcrumbs from '../../components/breadcrumbs/breadcrumbs';
import BundleItemProductDescription from '../../components/bundle-item-product-description/bundle-item-product-description';
import CECContactUs, {
  CECContactUsTheme
} from '../../components/cec-contact-us/cec-contact-us';
import FitmentPageBanner from '../../components/fitment-page-banner/fitment-page-banner';
import FoundItLowerButton, {
  FoundItLowerArrowOrientation,
  FoundItLowerTheme
} from '../../components/found-it-lower-button/found-it-lower-button';
import InstallationCostsMessage from '../../components/installation-costs-message/installation-costs-message';
import LazyLoad from '../../components/lazy-load/lazy-load';
import LoadingSpinner from '../../components/loading-spinner/loading-spinner';
import MinimumPriceMessage from '../../components/minimum-price-message/minimum-price-message';
import MyStoreMarker from '../../components/my-store-marker/my-store-marker';
import PackageIncludesMessage from '../../components/package-includes-message/package-includes-message';
import ParentProductMessage from '../../components/parent-product-message/parent-product-message';
import PdpTreadwellSection from '../../components/pdp-treadwell-section/pdp-treadwell-section';
import PhoneNumber from '../../components/phone-number/phone-number';
import Price from '../../components/price/price';
import ProductArticleStatusBanner from '../../components/product-article-status-banner/product-article-status-banner';
import ProductAvailabilityMessage from '../../components/product-availability-message/product-availability-message';
import ProductContainsDescription from '../../components/product-contains-description/product-contains-description';
import ProductDescription from '../../components/product-description/product-description';
import ProductDetails from '../../components/product-details/product-details';
import ProductFirstPriorityDetails from '../../components/product-first-priority-details/product-first-priority-details.one-dt';
import ProductFlags from '../../components/product-flags/product-flags';
import ProductImage from '../../components/product-image/product-image';
import ProductImageGallery from '../../components/product-image-gallery/product-image-gallery';
import ProductOECompatible from '../../components/product-oe-compatible/product-oe-compatible';
import ProductPromotions from '../../components/product-promotions/product-promotions';
import ProductQuantity from '../../components/product-quantity/product-quantity';
import ProductRating from '../../components/product-rating/product-rating';
import ProductReviews from '../../components/product-reviews/product-reviews';
import ProductSetDescription from '../../components/product-set-description/product-set-description';
import ProductSetImage from '../../components/product-set-image/product-set-image';
import ProductSwatchDescription from '../../components/product-swatch-description/product-swatch-description';
import QuantityDisclaimer from '../../components/quantity-disclaimer/quantity-disclaimer';
import ShopReplacementButton from '../../components/shop-replacement-button/shop-replacement-button';
import SkeletonElement from '../../components/skeleton-element/skeleton-element';
import StackedMarkdownPrice from '../../components/stacked-markdown-price/stacked-markdown-price';
import TireAttributeIcons from '../../components/tire-attribute-icons/tire-attribute-icons';
import TireWheelPackageDetails from '../../components/tire-wheel-package-details/tire-wheel-package-details';
import TpmsAccessoryMessage from '../../components/tpms-accessory-message/tpms-accessory-message';
import TreadwellProductExplanation, {
  RequestTypeEnum
} from '../../components/treadwell-product-explanation/treadwell-product-explanation';
import ViewOnVehicleButton from '../../components/view-on-vehicle-button/view-on-vehicle-button';
import CanonicalAccessoryForm from './components/canonical-accessory-form/canonical-accessory-form';
import CanonicalTireForm from './components/canonical-tire-form/canonical-tire-form';
import CanonicalWheelForm from './components/canonical-wheel-form/canonical-wheel-form';
import CollapsibleFitmentPageBanner from './components/collapsible-fitment-page-banner/collapsible-fitment-page-banner';
import MyAccountSavingsAppliedCartMessage from './components/my-account-savings-applied-cart-message/my-account-savings-applied-cart-message';
import PdpAddToCartSection from './components/pdp-add-to-cart-section/pdp-add-to-cart-section';
import PdpDealsSection from './components/pdp-deals-section/pdp-deals-section';
import PdpFeatureIcons from './components/pdp-feature-icons/pdp-feature-icons';
import PDPFreeServices from './components/pdp-free-services/pdp-free-services';
import PdpGallerySection from './components/pdp-gallery-section/pdp-gallery-section';
import StickyAddToCartSection from './components/sticky-add-to-cart-section/sticky-add-to-cart-section';
import StickyAddToCartSectionOneDt from './components/sticky-add-to-cart-section/sticky-add-to-cart-section.one-dt';
import StoreAvailabilityOptions from './components/store-availability-options/store-availability-options';
import DefaultLayout from '../../layouts/default-layout/default-layout';
import CarWrenchRegularIcon from '../../shared/icons/car-wrench-regular-icon';
import ExclamationTriangleSolidIcon from '../../shared/icons/exclamation-triangle-solid-icon';
import TruckFastRegularIcon from '../../shared/icons/truck-fast-regular-icon';

import {
  useCompareProducts,
  useDataLayer,
  useDoesProductFit,
  useFeatureFlags,
  useFitment,
  useMediaQuery,
  useModal,
  useMyAccount,
  useMyStore,
  useNavigateTo,
  usePrevious,
  useProductEntryDataLayerValues,
  useProductQuantity,
  useProductSwatchSelection,
  useScrollTo,
  useTireWheelPackage,
  useTreadwellPreferences
} from '../../shared/hooks/hooks';
import usePDPAnalytics from './hooks/use-pdp-analytics/use-pdp-analytics';
import { usePDPCMSContent } from './hooks/use-pdp-cms-content/use-pdp-cms-content';
import usePDPGalleryImages from './hooks/use-pdp-gallery-images/use-pdp-gallery-images';
import usePDPProduct from './hooks/use-pdp-product/use-pdp-product';
import usePDPReviews from './hooks/use-pdp-reviews/use-pdp-reviews';

import { trackAction } from '../../shared/utils/analytics/analytics';
import {
  getStoredBackToResultsUrl,
  removeStoredBackToResultsUrl,
  setPlpScrollToHash
} from '../../shared/utils/back-to-results/back-to-results';
import { isProductTrailerOrLawn } from '../../shared/utils/cart/cart';
import { interpolateCmsString } from '../../shared/utils/cms/cms';
import {
  getVehicleInput,
  parseTireSize
} from '../../shared/utils/fitment/fitment';
import { formatToTitleCase } from '../../shared/utils/format/format';
import { metaTags as getMetaTags } from '../../shared/utils/page/page';
import {
  getBackToResultsUrlFromProduct,
  getCombinedMapPrice,
  getNormalizedProductType,
  getPriceInfo,
  getPrioritizedArticleStatusProduct,
  getProductFitmentPosition,
  getProductImpressions,
  getProductPromotions,
  getProductSet,
  getProductSetItem,
  getProductWarranty,
  getShouldShowMinimumPriceMessage,
  getShouldShowProductArticleStatusBanner,
  getShouldShowProductAvailability,
  getTireAttributeIcons,
  getTreadwellAITrackActionPrefix,
  getWheelFinish,
  isCanonicalProduct as isCanonicalProductFunc,
  isWindShieldWiperProduct
} from '../../shared/utils/product/product';
import {
  getFilterOptions,
  getGalleryImagesByProductType,
  getOpenGraphProductTags,
  getProductLD,
  getSuggestedSellingAxleType,
  getSuggestedSellingPageContext
} from '../../shared/utils/product-detail-utils/product-detail-utils';
import {
  localStorage,
  sessionStorage
} from '../../shared/utils/storage/storage';
import { getStoreFeaturedServices } from '../../shared/utils/store/store';
import { isArrayWithLength } from '../../shared/utils/validators/validators';

import { ProductsForComparison } from '../../shared/types/contexts';
import { BundleType, ProductData } from '../../shared/types/graphql-types';
import { BACK_TO_RESULTS_LINK_VERBIAGE } from '../../shared/constants/back-to-results';
import { FEATURE_FLAGS } from '../../shared/constants/feature-flags';
import { DONT_FIT, FIT } from '../../shared/constants/fit';
import { SELECT_NEW_STORE_MODAL } from '../../shared/constants/modals';
import { PageTypes } from '../../shared/constants/page-types';
import {
  ACCESSORY_TYPES,
  ARTICLE_STATUSES,
  PRODUCT_TYPES,
  QUERY_TYPES,
  TREADWELL_AI_TRACK_ACTION_KEYS
} from '../../shared/constants/products';
import {
  PDP_ABANDONMENT_DATA,
  SALESFORCE_SUBSCRIBER_ID,
  SELECTED_PRODUCT_AVAILABILITY_STORE
} from '../../shared/constants/storage-keys';
import { SERVICE_CODE } from '../../shared/constants/store';

import './product-detail-page.scss';

const ProductCompareDrawer = lazy(
  () => import('../../components/product-compare-drawer/product-compare-drawer')
);

const SuggestedSellingPageSection = lazy(
  () =>
    import(
      '../../components/suggested-selling-page-section/suggested-selling-page-section'
    )
);

const CUSTOMER_REVIEWS_ELEMENT_ID = 'read-reviews';
const TREADWELL_DATA_ELEMENT_ID = 'treadwell-data';
const PRODUCT_FEATURES_ID = 'tire-attribute-icons';

const FEATURED_SERVICES_FOR_PDP = {
  [SERVICE_CODE.MOBILE_INSTALLATION]: {
    icon: <TruckFastRegularIcon />,
    linkDescriptionEnd: ' for an additional fee!',
    linkDescriptionStart: 'Save time with',
    linkText: 'mobile install'
  },
  [SERVICE_CODE.WHEEL_ALIGNMENT]: {
    icon: <CarWrenchRegularIcon />,
    linkDescriptionEnd: '. Free inspections!',
    linkDescriptionStart: 'Extend the life of your tires with an',
    linkText: 'alignment'
  }
};

interface ProductCodeParams {
  productCode?: string;
  productType?: string;
  rearProductCode?: string;
}

interface LocationState {
  initialProductCodes?: string[];
  prevPage?: string;
}

function ProductDetailPage() {
  const sholdScrollToReviewsOnPageLoadRef = useRef(true);
  const sholdScrollToTreadWellDataOnPageLoadRef = useRef(true);
  const shouldScrollToProductFeaturesRef = useRef(true);
  const [shouldScrollToReviews, setShouldScrollToReviews] = useState(false);
  const navigateTo = useNavigateTo();
  const { myStore, nearbyStorePreferences } = useMyStore();
  const { isFleetAccount, user } = useMyAccount();
  const filteredServices = getStoreFeaturedServices(myStore, isFleetAccount);
  const storeCode = window.isPreRendering ? '1344' : myStore?.code;
  const params = useParams<ProductCodeParams>();
  const { productCode, rearProductCode } = params;
  const scrollToReviews = useScrollTo(`#${CUSTOMER_REVIEWS_ELEMENT_ID}`, {
    offset: myStore?.pitPass ? 180 : 120
  });
  const scrollToTreadwell = useScrollTo(`#${TREADWELL_DATA_ELEMENT_ID}`, {
    offset: myStore?.pitPass ? 180 : 120
  });
  const scrollToProductFeatures = useScrollTo(`#${PRODUCT_FEATURES_ID}`, {
    offset: myStore?.pitPass ? 180 : 120
  });

  const hasProductCodeParams = Boolean(productCode || rearProductCode);
  const breakpoint = useMediaQuery();
  const { featureFlags } = useFeatureFlags();
  const hasTreadwellAI = Boolean(
    featureFlags?.[FEATURE_FLAGS.__FF_TREADWELL_AI_PDP__]
  );

  const { isTreadwellFlow, searchTypes, treadwellPreferences } =
    useTreadwellPreferences();
  const { getProductEntryDataLayerValues } = useProductEntryDataLayerValues();
  const { getPdpDataLayer } = usePDPAnalytics();
  const { selectedFitment } = useFitment();
  const previousSelectedFitment = usePrevious(selectedFitment, true);
  const previousStoreCode = usePrevious(myStore?.code, true);
  const { hash, pathname, state } = useLocation<LocationState>();
  const doesFitEnum = useRef<string | undefined>();
  const previousHashRef = useRef<string | undefined>(hash);
  const { updateDataLayer } = useDataLayer({
    shouldResetPageOnFitmentChange: true
  });
  const {
    error: productError,
    isCanonicalSearch,
    isCanonicalWithoutFilterOptions,
    isLoading: isLoadingProduct,
    isNoLongerAvailable,
    isPrebuiltPackage,
    isRefreshingData,
    isSetSearch,
    isStaggered,
    product,
    queryFilters,
    refetchData,
    setQueryFilters,
    weatherDetails
  } = usePDPProduct(searchTypes, treadwellPreferences, isTreadwellFlow);
  const {
    clearAllProductsForComparison,
    onProductComparison,
    showCompareDrawer,
    updateShowCompareDrawer
  } = useCompareProducts();
  const { openModal } = useModal();
  const isSet = isArrayWithLength(product?.associatedSet);
  const productSet = getProductSet(product);

  const shouldShowPdpStickyAddToCart = !breakpoint.isMinSm;
  const prebuiltPackage = isPrebuiltPackage ? product : null;

  const { currentTireWheelPackageFlow } = useTireWheelPackage();
  const productType = getNormalizedProductType(product);
  const [canonicalProductType, setCanonicalProductType] = useState();
  const isCanonicalProduct = isCanonicalProductFunc(product);
  const pageType = getNormalizedProductType(params?.productType);
  const [selectedTabIndex, setSelectedTabIndex] = useState(1);
  const [skipIsOtherVehicleOE, setSkipIsOtherVehicleOE] = useState(true);
  const [selectedStoreCode, setSelectedStoreCode] = useState<
    string | null | undefined
  >();
  const prevStoreCodeRef = useRef<string>(
    selectedStoreCode || myStore?.code || ''
  );

  const prevPageUrl = useRef(state?.prevPage).current;

  const hasSwatches = Boolean(product?.swatches);
  // TODO: Replace useMemo with useState/useEffect to populate swatches once
  // a product has swatches. Change to fix the disable caused UI to display
  // last set of swatches instead of first set
  // eslint-disable-next-line
  const swatches = useMemo(getSwatches, [hasSwatches]);
  function getSwatches() {
    if (product?.swatches) {
      return product.swatches;
    }
    return '';
  }

  const { saveSelectedSwatch, selectedSwatch } = useProductSwatchSelection(
    product?.code,
    swatches,
    true
  );

  const backToResultsContext = getStoredBackToResultsUrl('');

  const plpUrl =
    backToResultsContext?.url || getBackToResultsUrlFromProduct(product);
  const isPlpUrlStaggered = /\/staggered|rear\//gi.test(plpUrl);

  const isFromVlp =
    prevPageUrl && prevPageUrl.includes('vehicle-') ? true : false;
  const vlpUrl = prevPageUrl ? prevPageUrl + '#vlp-product-spotlight' : '/';

  const isAccessory = productType === PRODUCT_TYPES.ACCESSORIES;
  let bundleType: BundleType | null = null;

  if (selectedFitment?.isStaggered || isStaggered || isPlpUrlStaggered) {
    bundleType = BundleType.StaggeredSet;
  } else if (isPrebuiltPackage) {
    bundleType = BundleType.TireWheelPackage;
  }

  const {
    defaultQuantity,
    productSetQuantity,
    quantity,
    rearQuantity,
    setProductSetQuantity,
    setQuantity,
    setRearQuantity,
    totalQuantity
  } = useProductQuantity({
    articleServiceType: product?.articleServiceType,
    bundleType: bundleType ?? undefined,
    initialSetQuantity: isSet
      ? productSet?.map((_, idx) => (idx === 0 || isSetSearch ? 1 : 0))
      : undefined,
    productType
  });
  const productSetItem = getProductSetItem(
    productSet,
    productSetQuantity,
    totalQuantity
  );

  const [shouldSetPlpScrollToHash, setShouldSetPlpScrollToHash] =
    useState(true);

  function getCmsPageId() {
    // we always use the products url for the cms page id
    if (isCanonicalProduct) {
      return process.env.__FF_PDP_CMS_DOCUMENT_TITLE__ === 'true'
        ? pathname
        : `${pathname}/p/${product?.aggregates?.codes[0]}`;
    } else if (isPrebuiltPackage) {
      return getNormalizedProductType(
        prebuiltPackage?.product1?.productType
      ) === PRODUCT_TYPES.TIRES
        ? prebuiltPackage.product1?.url
        : prebuiltPackage?.product2?.url;
    } else {
      return product?.url;
    }
  }

  const {
    documentTitle: cmsDocumentTitle,
    hasPDPCMSContentloaded,
    metaTags: cmsMetaTags,
    overviewHeading,
    services,
    servicesHeading,
    tooltips
  } = usePDPCMSContent(getCmsPageId());

  const {
    aggregates,
    brand,
    brandLogo,
    code,
    gbb,
    mapDisplayRule,
    mapPrice,
    mapRuleSatisfied,
    name,
    origEquipmentData,
    priceRange,
    rearProduct,
    recommendationData: productRecommendationData,
    size,
    tdsData,
    tireWheelRimDiameter,
    unitName,
    unitOfMeasureDescription,
    url,
    vendorFinish
  } = product || {};

  const showTreadwellProductExplanation =
    hasTreadwellAI &&
    productType === PRODUCT_TYPES.TIRES &&
    !treadwellPreferences?.showWinterTires;

  const hasTdsStoppingData =
    Boolean(tdsData?.stopping?.dryRating) &&
    Boolean(tdsData?.stopping?.wetRating);
  const hasTdsWinterData =
    Boolean(tdsData?.winter?.snowHandlingRating) &&
    Boolean(tdsData?.winter?.snowBrakeFeet);

  const articleStatusProduct = isPrebuiltPackage
    ? getPrioritizedArticleStatusProduct(
        prebuiltPackage?.product1,
        prebuiltPackage?.product2
      )
    : getPrioritizedArticleStatusProduct(product, rearProduct);
  const shouldShowProductArticleStatusBanner =
    getShouldShowProductArticleStatusBanner(articleStatusProduct);

  const shouldShowProductAvailability =
    getShouldShowProductAvailability(articleStatusProduct);

  const showMinimumPriceMessage = getShouldShowMinimumPriceMessage(product);
  const showRearMinimumPriceMessage =
    getShouldShowMinimumPriceMessage(rearProduct);

  const showAddToCartComponents = !articleStatusProduct?.addToCartDisabled;
  const price =
    product?.price?.value ||
    prebuiltPackage?.product1?.price?.value * 4 +
      prebuiltPackage?.product2?.price?.value * 4;
  const { code: rearCode, size: rearSize } = rearProduct || {};
  const frontSize = isWindShieldWiperProduct(product) ? `${size}"` : size;
  const titleSize = hasProductCodeParams
    ? `${frontSize || ''} ${frontSize && rearSize ? '|' : ''} ${
        rearSize || ''
      }`.trim()
    : '';
  const rearPrice = rearProduct?.price?.value;

  let productCodes = aggregates?.codes || [code];
  if (isPrebuiltPackage && prebuiltPackage) {
    productCodes = [
      prebuiltPackage.product1?.code,
      prebuiltPackage.product2?.code
    ];
  }
  if (isStaggered) {
    productCodes = [{ front: code, rear: rearCode }];
  } else if (isSet) {
    productCodes = [
      product?.code,
      ...(product?.associatedSet?.map(product => product?.code) || [])
    ];
  }

  const isOe = isStaggered
    ? !isEmpty(origEquipmentData) && !isEmpty(rearProduct?.origEquipmentData)
    : !isEmpty(origEquipmentData);

  const priceInfo = getPriceInfo(product);
  const rearPriceInfo = getPriceInfo(rearProduct);

  const tirePriceInfo = isPrebuiltPackage
    ? getPriceInfo(prebuiltPackage?.product1)
    : null;
  const wheelPriceInfo = isPrebuiltPackage
    ? getPriceInfo(prebuiltPackage?.product2)
    : null;

  const {
    canonicalDoesFitEnum,
    doesFitEnumMap,
    isLoading: isLoadingDoesFit,
    isOtherVehiclesOE
  } = useDoesProductFit({
    fitment: selectedFitment,
    isCanonical: isCanonicalSearch,
    isStaggered,
    productCodes,
    skipIsOtherVehicleOE
  });

  const doesStoreSupportCommercialVehicle = useMemo(
    () =>
      myStore?.additionalServices?.some(
        service => service?.code === SERVICE_CODE.COMMERCIAL_VEHICLE
      ),
    [myStore]
  );
  const isUnsupportedCommercialProduct =
    !doesStoreSupportCommercialVehicle && tireWheelRimDiameter === '19.5';

  function getPrebuiltPackageProduct(productType) {
    if (!prebuiltPackage) {
      return;
    }

    if (
      getNormalizedProductType(prebuiltPackage.product1?.productType) ===
      productType
    ) {
      return prebuiltPackage?.product1;
    }

    if (
      getNormalizedProductType(prebuiltPackage.product2?.productType) ===
      productType
    ) {
      return prebuiltPackage?.product2;
    }
  }

  const onFilterChange = useCallback(
    selectedFilters => {
      const filterOptions = getFilterOptions(selectedFilters);

      if (Object.keys(selectedFilters).length > 0) {
        setQueryFilters(filterOptions);
      }
    },
    [setQueryFilters]
  );

  const prebuiltPackageRecommendationData = getPrebuiltPackageProduct(
    PRODUCT_TYPES.TIRES
  )?.recommendationData;

  const recommendationData =
    productRecommendationData || prebuiltPackageRecommendationData || tdsData;
  const {
    frontProduct: topFrontRecommendedProduct,
    rearProduct: topRearRecommendedProduct
  } = recommendationData?.topRecommendedProductData || {};
  const explanationProducts = useMemo(
    () => [
      product,
      ...(topFrontRecommendedProduct
        ? [
            {
              ...topFrontRecommendedProduct,
              rearProduct: topRearRecommendedProduct
            }
          ]
        : [])
    ],
    [product, topFrontRecommendedProduct, topRearRecommendedProduct]
  );
  const showExplanationCompareSection = explanationProducts.length === 2;
  const explanationRequestType = showExplanationCompareSection
    ? RequestTypeEnum.ContextualCompare
    : RequestTypeEnum.Explain;

  const {
    averageRatings,
    hasProductLineReviews,
    includeVariantProductReviews,
    isLoadingReviews,
    productReviews,
    reviewSummary,
    reviews,
    setIncludeVariantProductReviews,
    setPageNumber,
    setReviewsFilterSortQuery
  } = usePDPReviews({
    productCodes: isStaggered ? [code, rearCode] : productCodes,
    rearProductCode
  });
  const productRating = reviewSummary?.totalRating || 0;
  const productReviewCount = reviewSummary?.totalCount || 0;
  const icons = getTireAttributeIcons(product);
  const suggestedSellingAxleType = getSuggestedSellingAxleType(
    prevPageUrl,
    selectedFitment?.isStaggered
  );
  const suggestedSellingPageContext = getSuggestedSellingPageContext(
    pageType,
    product?.name,
    isTreadwellFlow
  );

  const mapPriceProps = {
    mapPrice: isPrebuiltPackage ? tirePriceInfo?.mapPrice : priceInfo?.mapPrice,
    price: isPrebuiltPackage ? tirePriceInfo?.price : priceInfo?.price,
    quantity: isPrebuiltPackage ? quantity * 4 : quantity,
    rearMapPrice: isPrebuiltPackage
      ? wheelPriceInfo?.mapPrice
      : rearPriceInfo?.mapPrice,
    rearPrice: isPrebuiltPackage ? wheelPriceInfo?.price : rearPriceInfo?.price,
    rearQuantity: isPrebuiltPackage ? quantity * 4 : rearQuantity
  };
  const combinedMapPrice = getCombinedMapPrice(mapPriceProps);

  const promos = getProductPromotions(product, isPrebuiltPackage);

  function updateQuantity(e, quantity, index?: number) {
    if (typeof index === 'number') {
      setProductSetQuantity(quantity, index);
    } else {
      setQuantity(quantity);
    }
  }

  function updateRearQuantity(e, quantity) {
    setRearQuantity(quantity);
  }

  function onReadReviewsClick() {
    forceVisible();
    setShouldScrollToReviews(true);
    trackAction('product_aggregate_reviews');
  }

  function formatBrand(brand) {
    return brand.toLowerCase().replace(/\s+/g, '-');
  }

  function getProductTypeUrl(product) {
    if (prebuiltPackage) {
      return '/packages';
    }

    switch (productType) {
      case PRODUCT_TYPES.TIRES:
      case PRODUCT_TYPES.WHEELS:
        return `/${productType.toLowerCase()}/brands/${formatBrand(
          product.brand
        )}`;
      case PRODUCT_TYPES.ACCESSORIES:
        return `/${productType.toLowerCase()}`;
      default:
        return '/';
    }
  }
  const [inputType, setInputType] = useState('');

  const handleProductComparison = useCallback(() => {
    const [product1, product2]: Array<ProductData> = explanationProducts.reduce<
      Array<ProductData>
    >((arr, curr) => {
      if (curr) {
        if (curr?.recommendationData?.topRecommendedProductData) {
          arr.unshift(curr);
        } else {
          arr.push(curr);
        }
      }

      return arr;
    }, []);

    if (product1 && product2) {
      const trackActionIdPrefix = getTreadwellAITrackActionPrefix(
        PageTypes.PDP,
        false
      );
      trackAction(`${trackActionIdPrefix}_see_why_product_compare_click`, {
        [TREADWELL_AI_TRACK_ACTION_KEYS.MESSAGE]: 'compare to top recommended'
      });
      clearAllProductsForComparison();
      onProductComparison(product1 as ProductsForComparison, true);
      onProductComparison(product2 as ProductsForComparison, true);
      updateShowCompareDrawer(true);
    }
  }, [
    clearAllProductsForComparison,
    explanationProducts,
    onProductComparison,
    updateShowCompareDrawer
  ]);

  useEffect(() => {
    let newInputType;

    if (isPrebuiltPackage) {
      newInputType = 'PACKAGE';
    } else if (productType === PRODUCT_TYPES.TIRES) {
      newInputType = isStaggered ? 'TIRE_SET' : 'TIRE';
    } else if (productType === PRODUCT_TYPES.WHEELS) {
      newInputType = isStaggered ? 'WHEEL_SET' : 'WHEEL';
    } else if (productType === PRODUCT_TYPES.ACCESSORIES) {
      newInputType = 'ACCESSORY';
    }

    setInputType(newInputType);
  }, [isPrebuiltPackage, productType, isStaggered, product?.code]);

  useEffect(() => {
    const subscriberId = localStorage(SALESFORCE_SUBSCRIBER_ID);
    const email = user?.email;

    function getPrebuiltProductCodes(product) {
      if (product?.product1 && product?.product2) {
        return [product?.product1?.code, product?.product2?.code];
      }
      return [];
    }

    const productCodes = getPrebuiltProductCodes(product);
    if (
      storeCode &&
      (isPrebuiltPackage ? productCodes.length === 2 : product?.code) &&
      (subscriberId || email) &&
      inputType
    ) {
      localStorage(PDP_ABANDONMENT_DATA, {
        pdpInput: {
          dryStoppingDistance: recommendationData?.stopping?.dryDistance,
          email,
          inputType,
          pdpUrl: window.location.href,
          productCode1: isPrebuiltPackage ? productCodes[0] : product?.code,
          productCode2: isPrebuiltPackage ? productCodes[1] : rearProduct?.code,
          rating: reviewSummary?.totalRating || 0,
          reviewCount: reviewSummary?.totalCount || 0,
          subscriberId,
          wetStoppingDistance: recommendationData?.stopping?.wetDistance
        },
        storeCode,
        vehicleInfo: getVehicleInput(selectedFitment)
      });
    }
  }, [
    inputType,
    isPrebuiltPackage,
    product,
    rearProduct,
    recommendationData,
    reviewSummary,
    selectedFitment,
    storeCode,
    user?.email
  ]);

  useEffect(() => {
    if (
      queryFilters &&
      previousSelectedFitment !== selectedFitment &&
      state?.initialProductCodes
    ) {
      if (state?.initialProductCodes) {
        // remove initialProductCodes from browser history state
        delete state.initialProductCodes;
      }
    }
  }, [state, queryFilters, previousSelectedFitment, selectedFitment]);

  useEffect(() => {
    // if a canonical search does not have an aggregate product in its initial result, then the user belongs
    // on the single-product PDP
    if (
      isCanonicalSearch &&
      isCanonicalWithoutFilterOptions &&
      product &&
      product.productType !== 'aggregate'
    ) {
      navigateTo(`${window.location.pathname}/p/${product.code}`, true);
    }
  }, [isCanonicalSearch, isCanonicalWithoutFilterOptions, product, navigateTo]);

  // track the action of drilling the canonical PDP down to a single article
  useEffect(() => {
    const { isNoLongerAvailable, productType: newCanonicalProductType } =
      product || {};

    if (
      newCanonicalProductType &&
      newCanonicalProductType !== canonicalProductType
    ) {
      if (
        canonicalProductType === 'aggregate' &&
        newCanonicalProductType !== 'aggregate'
      ) {
        updateDataLayer(
          getPdpDataLayer({
            isNoLongerAvailable: Boolean(isNoLongerAvailable),
            product,
            selectedStoreCode: selectedStoreCode
          }),
          false
        );
      }

      setCanonicalProductType(newCanonicalProductType);
    }
  }, [
    canonicalProductType,
    getPdpDataLayer,
    isNoLongerAvailable,
    product,
    quantity,
    selectedStoreCode,
    updateDataLayer
  ]);

  useEffect(() => {
    if (selectedFitment?.assemblyId !== previousSelectedFitment?.assemblyId) {
      setSkipIsOtherVehicleOE(false);
    }
  }, [selectedFitment, previousSelectedFitment, product]);

  const {
    fallbackProductImage,
    galleryImages,
    metaImage,
    showFallbackProductImage
  } = usePDPGalleryImages(
    product,
    productType,
    isPrebuiltPackage,
    isCanonicalSearch,
    selectedSwatch
  );

  // TODO: This code will be removed once the image data is fixed from BE.
  const defaultProductImage = isArrayWithLength(product?.images)
    ? product?.images?.[0]?.url
    : fallbackProductImage;

  const replacementProduct = product?.productReferences?.[0]?.target;
  const replacementProductPromos = replacementProduct?.potentialPromotions;

  useEffect(() => {
    if (!product) {
      return;
    }
    const productPath = product?.url?.replace(/\?.+/, '');
    let currentPath: string | undefined;

    if (isSet) {
      currentPath = `${productPath}/set`;
    } else {
      currentPath = productPath;
    }

    if (!isLoadingProduct && currentPath === pathname) {
      if (isNoLongerAvailable) {
        trackAction(
          'product_detail_nla_view',
          getProductEntryDataLayerValues(product, quantity)
        );

        if (replacementProduct) {
          trackAction(
            'product_nla_replacement_product_view',
            getProductImpressions(
              [replacementProduct].map(getProductEntryDataLayerValues),
              pathname
            )
          );
        }
      }

      updateDataLayer(
        getPdpDataLayer({
          isNoLongerAvailable,
          product,
          selectedStoreCode: selectedStoreCode,
          treadwellPreferences,
          weatherDetails
        }),
        !(
          prevStoreCodeRef.current !== selectedStoreCode ||
          (hash === '' && previousHashRef.current === '#/wheel-visualizer')
        )
      );

      if (selectedStoreCode) {
        prevStoreCodeRef!.current = selectedStoreCode;
      }

      if (hash !== previousHashRef.current) {
        previousHashRef.current = hash;
      }
    }
  }, [
    isSet,
    pathname,
    getPdpDataLayer,
    getProductEntryDataLayerValues,
    isLoadingProduct,
    isNoLongerAvailable,
    product,
    quantity,
    replacementProduct,
    treadwellPreferences,
    updateDataLayer,
    weatherDetails,
    selectedStoreCode,
    hash
  ]);

  useEffect(() => {
    if (shouldSetPlpScrollToHash) {
      if (canonicalProductType) {
        setShouldSetPlpScrollToHash(false);
      }
      setPlpScrollToHash(code);
    }
  }, [shouldSetPlpScrollToHash, code, canonicalProductType]);

  useEffect(() => {
    if (
      sholdScrollToReviewsOnPageLoadRef.current &&
      hash === `#${CUSTOMER_REVIEWS_ELEMENT_ID}` &&
      !isLoadingProduct &&
      hasPDPCMSContentloaded &&
      !isLoadingReviews
    ) {
      forceVisible();
      setShouldScrollToReviews(true);
      sholdScrollToReviewsOnPageLoadRef.current = false;
    }
  }, [
    hash,
    isLoadingProduct,
    isLoadingReviews,
    scrollToReviews,
    hasPDPCMSContentloaded,
    isCanonicalProduct
  ]);

  useEffect(() => {
    if (
      hash === `#${TREADWELL_DATA_ELEMENT_ID}` &&
      sholdScrollToTreadWellDataOnPageLoadRef.current &&
      !isLoadingProduct
    ) {
      scrollToTreadwell();
      sholdScrollToTreadWellDataOnPageLoadRef.current = false;
    }
  }, [hash, isLoadingProduct, scrollToTreadwell]);

  useEffect(() => {
    if (
      hash === `#${PRODUCT_FEATURES_ID}` &&
      shouldScrollToProductFeaturesRef.current &&
      !isLoadingProduct
    ) {
      scrollToProductFeatures({});
      shouldScrollToProductFeaturesRef.current = false;
    }
  }, [hash, isLoadingProduct, scrollToProductFeatures]);

  useEffect(() => {
    if (shouldScrollToReviews) {
      scrollToReviews();
      setShouldScrollToReviews(false);
    }
  }, [scrollToReviews, shouldScrollToReviews]);

  useEffect(() => {
    if (!selectedStoreCode) {
      const selectedAvailabilityStore = sessionStorage(
        SELECTED_PRODUCT_AVAILABILITY_STORE
      );

      if (
        selectedAvailabilityStore &&
        selectedAvailabilityStore.availabilityStoreCode !== myStore?.code &&
        selectedAvailabilityStore.pdpUrl === pathname &&
        backToResultsContext?.pageId === 'PLP' &&
        Boolean(nearbyStorePreferences)
      ) {
        setSelectedStoreCode(articleStatusProduct?.nearByStoreStock?.storeCode);
      } else {
        setSelectedStoreCode(myStore?.code);
      }
    }
  }, [
    articleStatusProduct?.nearByStoreStock?.storeCode,
    backToResultsContext?.pageId,
    myStore?.code,
    nearbyStorePreferences,
    pathname,
    product,
    selectedStoreCode,
    treadwellPreferences,
    updateDataLayer,
    weatherDetails
  ]);

  useEffect(() => {
    if (myStore?.code !== previousStoreCode) {
      setSelectedStoreCode(myStore?.code);
      refetchData();
    }
  }, [
    selectedFitment,
    previousSelectedFitment,
    product,
    myStore?.code,
    previousStoreCode,
    refetchData
  ]);

  useEffect(() => {
    if (isUnsupportedCommercialProduct) {
      openModal(SELECT_NEW_STORE_MODAL, { myStore });
    }
  }, [isUnsupportedCommercialProduct, myStore, openModal]);

  const metaInterpolationMap = {
    attributes: isStaggered
      ? 'in staggered fitment '
      : `${frontSize} `.replace(/[\s]{2,}/g, ' ').trim(),
    siteName: window.siteName
  };

  const documentTitle = interpolateCmsString(
    metaInterpolationMap,
    cmsDocumentTitle
  );

  const metaTags =
    process.env.__FF_PDP_CMS_DOCUMENT_TITLE__ === 'true' &&
    isArrayWithLength(cmsMetaTags)
      ? cmsMetaTags.map(tag => ({
          ...tag,
          content: interpolateCmsString(metaInterpolationMap, tag.content)
        }))
      : cmsMetaTags;

  const productLD = getProductLD({
    imageUrl: metaImage,
    prebuiltPackage,
    product
  });

  const ogTags = getOpenGraphProductTags(
    metaTags,
    product,
    prebuiltPackage,
    metaImage
  );

  const treadLifeMedian = product?.recommendationData?.mileage?.median;

  let selectedPrebuiltPackageProduct;

  if (prebuiltPackage) {
    selectedPrebuiltPackageProduct =
      getNormalizedProductType(prebuiltPackage.product1?.productType) ===
        PRODUCT_TYPES.TIRES && selectedTabIndex === 1
        ? prebuiltPackage.product1
        : prebuiltPackage.product2;
  }

  if (!isLoadingDoesFit) {
    if (isCanonicalSearch) {
      doesFitEnum.current = canonicalDoesFitEnum;
    } else {
      doesFitEnum.current = Object.values(doesFitEnumMap || {}).every(
        v => v === FIT
      )
        ? FIT
        : DONT_FIT;
    }
  }

  if (productError && !window.isPreRendering) {
    throw new Error(productError);
  }

  if (isPrebuiltPackage && doesFitEnum.current === DONT_FIT) {
    return (
      <Redirect to="/fitmentresult/packages/prebuilt?q=:bestSeller-asc&sort=bestSeller-asc" />
    );
  }

  return (
    <DefaultLayout showMinimalFooter>
      {hasTreadwellAI && showCompareDrawer && (
        <Suspense fallback={<div />}>
          <ProductCompareDrawer
            isFitmentSearch
            isOpen={showCompareDrawer}
            isTreadwellExplanationOpenByDefault
            onAddToCompare={() => {
              updateShowCompareDrawer(false);
              removeStoredBackToResultsUrl();
              navigateTo(isFromVlp ? vlpUrl : plpUrl);
            }}
            onClose={() => {
              updateShowCompareDrawer(false);
              trackAction('product_detail_compare_drawer_close');
              clearAllProductsForComparison();
            }}
            pageType={PageTypes.PDP}
            productType={productType}
            queryType={QUERY_TYPES.PLP}
          />
        </Suspense>
      )}
      <Page styleName="page">
        {brand && name && productType && !window.isPreRendering && (
          <Helmet>
            {process.env.__FF_PDP_CMS_DOCUMENT_TITLE__ === 'true' &&
            documentTitle &&
            productType !== PRODUCT_TYPES.ACCESSORIES ? (
              <title>{documentTitle}</title>
            ) : (
              <title>
                {brand} {name} {titleSize} | {window.siteName}
              </title>
            )}

            <link
              href={`${window.location.protocol}//${process.env.__DT_HOST__}${
                pathname.split('/p/')[0]
              }`}
              rel="canonical"
            />
            <meta content="summary_large_image" name="twitter:card" />
            <meta content="@DiscountTire" name="twitter:site" />
            <meta content="@DiscountTire" name="twitter:creator" />
            {!metaTags?.some?.(m => m.name === 'description') && (
              <meta
                content={`Find ${brand} ${name} for your vehicle right here at ${window.siteName}.`}
                name="description"
              />
            )}
            {getMetaTags(metaTags)}
            {productLD && (
              <script type="application/ld+json">{productLD}</script>
            )}
            {ogTags}
          </Helmet>
        )}
        {product && (
          <Breadcrumbs
            crumbs={
              !prebuiltPackage
                ? [
                    { name: 'Home', url: '/' },
                    {
                      name:
                        productType === PRODUCT_TYPES.ACCESSORIES
                          ? formatToTitleCase(productType)
                          : product.brand,
                      url: getProductTypeUrl(product)
                    },
                    { name: product.name, url: '' }
                  ]
                : [
                    { name: 'Home', url: '/' },
                    {
                      name: 'Packages',
                      url: getProductTypeUrl(product)
                    }
                  ]
            }
            styleName="pdp-breadcrumbs"
          />
        )}
        {!isProductTrailerOrLawn(product) &&
          (breakpoint.isMinSm || !selectedFitment ? (
            <FitmentPageBanner
              doesFitEnum={doesFitEnum.current}
              isLoading={!doesFitEnum.current}
              isPackage={isPrebuiltPackage}
              isSingular={isPrebuiltPackage}
              productType={pageType}
              tireSizes={{
                front: parseTireSize(frontSize),
                rear: parseTireSize(rearSize)
              }}
            />
          ) : (
            <CollapsibleFitmentPageBanner
              doesFitEnum={doesFitEnum.current}
              isLoading={!doesFitEnum.current}
              isPackage={isPrebuiltPackage}
              productType={pageType}
              tireSizes={{
                front: parseTireSize(frontSize),
                rear: parseTireSize(rearSize)
              }}
            />
          ))}

        <TireWheelPackageDetails />

        <Content>
          <BackButton
            href={isFromVlp ? vlpUrl : plpUrl}
            onClick={removeStoredBackToResultsUrl}
            styleName="back-button"
            text={
              isFromVlp
                ? BACK_TO_RESULTS_LINK_VERBIAGE.VLP
                : BACK_TO_RESULTS_LINK_VERBIAGE[backToResultsContext?.pageId]
            }
          />
          <Section isFullWidth={breakpoint.isMinSm ? false : true}>
            {window.isPreRendering ||
            (!product && !prebuiltPackage && isLoadingProduct) ? (
              <div styleName="info">
                <SkeletonElement styleName="brand-skeleton" />
                <SkeletonElement styleName="name-skeleton" />
              </div>
            ) : (
              <>
                {(product || prebuiltPackage) && (
                  <div
                    styleName={
                      !isNoLongerAvailable ? 'info' : 'no-longer-available-info'
                    }
                  >
                    {!breakpoint.isMinSm && (
                      <div styleName="product-description">
                        <ProductFlags
                          hideTreadwellRank={!isTreadwellFlow}
                          prebuiltPackage={prebuiltPackage}
                          product={product}
                          styleName="product-flags"
                        />
                        <ProductDescription
                          brand={brand}
                          code={
                            isCanonicalProduct || isStaggered || isSet
                              ? null
                              : code
                          }
                          gbb={gbb?.toLowerCase()}
                          isOE={isOe}
                          link={`#${TREADWELL_DATA_ELEMENT_ID}`}
                          name={name}
                          productType={pageType}
                          showGBBTooltip
                          size={isStaggered || isSet ? null : frontSize}
                          styleName="product-description-top"
                        />
                        {pageType === PRODUCT_TYPES.TIRES && (
                          <div>
                            <ProductRating
                              avgReviewRating={productRating}
                              hideRating
                              linkText={productRating > 0 ? productRating : 0}
                              numberOfReviews={productReviewCount}
                              onReadReviewClick={onReadReviewsClick}
                              styleName="product-reviews"
                            />
                          </div>
                        )}
                      </div>
                    )}
                    <div styleName="gallery">
                      <PdpGallerySection
                        fallbackProductImage={defaultProductImage}
                        galleryImages={galleryImages}
                        hideTreadwellRank={!isTreadwellFlow}
                        isNoLongerAvailable={isNoLongerAvailable}
                        prebuiltPackage={prebuiltPackage}
                        product={product}
                        selectedSwatch={selectedSwatch}
                        showFallbackProductImage={showFallbackProductImage}
                      />
                      {breakpoint.isMinSm &&
                        productType !== PRODUCT_TYPES.ACCESSORIES &&
                        !prebuiltPackage && (
                          <CECContactUs
                            isTires={productType === PRODUCT_TYPES.TIRES}
                            theme={CECContactUsTheme.GREY}
                          />
                        )}

                      {breakpoint.isMinSm && showTreadwellProductExplanation && (
                        <TreadwellProductExplanation
                          compareSection={
                            showExplanationCompareSection && (
                              <div styleName="treadwell-product-explanation-comparison-container">
                                <Typography>
                                  See how this tire compares to the #1 Treadwell
                                  recommendation for your car, your driving
                                  area, and your driving style
                                </Typography>
                                <Button
                                  isTertiary
                                  onClick={handleProductComparison}
                                >
                                  See tire comparison
                                </Button>
                              </div>
                            )
                          }
                          pageType={PageTypes.PDP}
                          products={explanationProducts}
                          requestType={explanationRequestType}
                        />
                      )}
                    </div>
                    <div styleName="description">
                      {isPrebuiltPackage ? (
                        <>
                          {breakpoint.isMinSm && !isNoLongerAvailable && (
                            <PackageIncludesMessage isPrebuiltPackage />
                          )}
                          <BundleItemProductDescription
                            entry={{
                              product: prebuiltPackage.product1,
                              quantity: 4
                            }}
                            hideImage
                            hideQty
                            isPackage
                            isPrebuilt
                            link={pathname}
                            shouldDisplayTotalPrice
                            showInstallationCostsMessage
                            showMinimumPriceMessage
                            showProductCode
                            showQuantityDisclaimer
                            showViewOnVehicle
                          />
                          {(recommendationData ||
                            hasTdsStoppingData ||
                            hasTdsWinterData) &&
                            !isNoLongerAvailable && (
                              <ProductFirstPriorityDetails
                                dataReportUrl={url}
                                hideLink
                                onClick={() => {
                                  trackAction(
                                    'product_detail_tire_test_data_view'
                                  );
                                  scrollToTreadwell();
                                }}
                                product={product}
                              />
                            )}
                          {!isNoLongerAvailable && (
                            <div
                              id="tire-attribute-icons"
                              styleName="prebuilt-tire-attribute-icons"
                            >
                              <PdpFeatureIcons
                                icons={getTireAttributeIcons(
                                  prebuiltPackage.product1
                                )}
                                rollingResistanceRating={
                                  recommendationData?.stopping
                                    ?.rollingResistanceRating
                                }
                                warranty={getProductWarranty(
                                  prebuiltPackage.product1
                                )}
                              />
                            </div>
                          )}
                          {!breakpoint.isMinSm && (
                            <ProductImageGallery
                              fallbackProductImage={defaultProductImage}
                              galleryImages={getGalleryImagesByProductType(
                                galleryImages,
                                PRODUCT_TYPES.WHEELS
                              )}
                              product={prebuiltPackage.product2}
                              showFallbackProductImage={
                                showFallbackProductImage
                              }
                            />
                          )}
                          <BundleItemProductDescription
                            entry={{
                              product: prebuiltPackage.product2,
                              quantity: 4
                            }}
                            hideImage
                            hideQty
                            isPackage
                            isPrebuilt
                            link={pathname}
                            shouldDisplayTotalPrice
                            showInstallationCostsMessage
                            showMinimumPriceMessage
                            showProductCode
                            showQuantityDisclaimer
                            showViewOnVehicle
                          />
                          {!isNoLongerAvailable && (
                            <>
                              <ProductPromotions
                                brand={brand}
                                productType={productType}
                                promos={promos}
                                showDuration
                                showLongDescription
                                showTooltip
                              />
                              {!isCanonicalProduct &&
                                shouldShowProductAvailability && (
                                  <StoreAvailabilityOptions
                                    myStoreCode={myStore?.code}
                                    onClickStore={setSelectedStoreCode}
                                    prebuiltPackage={prebuiltPackage}
                                    product={articleStatusProduct}
                                    quantity={quantity}
                                    rearProduct={rearProduct}
                                    rearQuantity={rearQuantity}
                                    selectedStoreCode={selectedStoreCode}
                                  />
                                )}
                              <PdpDealsSection
                                combinedMapPrice={combinedMapPrice}
                                isAddToCartDisabled={!showAddToCartComponents}
                                isCanonicalProduct={isCanonicalProduct}
                                promos={promos}
                              />
                              {isArrayWithLength(services) && (
                                <PDPFreeServices
                                  heading={servicesHeading}
                                  services={services}
                                />
                              )}
                              <MyAccountSavingsAppliedCartMessage
                                potentialPromotions={promos}
                              />
                              {showAddToCartComponents &&
                                !shouldShowPdpStickyAddToCart && (
                                  <PdpAddToCartSection
                                    defaultQuantity={defaultQuantity}
                                    isCanonicalProduct={isCanonicalProduct}
                                    isDisabled={isUnsupportedCommercialProduct}
                                    isStaggered={isStaggered}
                                    myStore={myStore}
                                    productItem={{
                                      prebuiltPackage,
                                      product,
                                      quantity,
                                      rearProduct,
                                      rearQuantity,
                                      totalQuantity
                                    }}
                                    productType={productType}
                                    storeCode={selectedStoreCode}
                                    updateQuantity={updateQuantity}
                                  />
                                )}

                              {showAddToCartComponents &&
                                shouldShowPdpStickyAddToCart &&
                                (process.env.__FF_UPDATE_PDP_STICKY_FOOTER__ ===
                                'true' ? (
                                  <StickyAddToCartSectionOneDt
                                    defaultQuantity={defaultQuantity}
                                    isCanonicalProduct={isCanonicalProduct}
                                    isDisabled={isUnsupportedCommercialProduct}
                                    price={price}
                                    productItem={{
                                      prebuiltPackage,
                                      product,
                                      quantity,
                                      rearProduct,
                                      rearQuantity,
                                      totalQuantity
                                    }}
                                    productType={productType}
                                    selectedFitment={selectedFitment}
                                    storeCode={selectedStoreCode}
                                    updateQuantity={updateQuantity}
                                  />
                                ) : (
                                  <StickyAddToCartSection
                                    defaultQuantity={defaultQuantity}
                                    isCanonicalProduct={isCanonicalProduct}
                                    isDisabled={isUnsupportedCommercialProduct}
                                    isSet={isSet}
                                    isStaggered={isStaggered}
                                    price={price}
                                    productItem={{
                                      prebuiltPackage,
                                      product,
                                      quantity,
                                      rearProduct,
                                      rearQuantity,
                                      totalQuantity
                                    }}
                                    productType={productType}
                                    selectedFitment={selectedFitment}
                                    storeCode={selectedStoreCode}
                                    updateQuantity={updateQuantity}
                                  />
                                ))}
                            </>
                          )}
                        </>
                      ) : (
                        <>
                          {breakpoint.isMinSm && (
                            <div styleName="product-description">
                              <ProductDescription
                                brand={brand}
                                brandLogoUrl={brandLogo?.url}
                                code={
                                  isCanonicalProduct || isStaggered || isSet
                                    ? null
                                    : code
                                }
                                gbb={gbb?.toLowerCase()}
                                isOE={isOe}
                                link={`#${TREADWELL_DATA_ELEMENT_ID}`}
                                name={name}
                                productType={pageType}
                                showGBBTooltip
                                size={isStaggered || isSet ? null : frontSize}
                              />
                            </div>
                          )}
                          {!isNoLongerAvailable && (
                            <>
                              {!isStaggered && !isSet && !breakpoint.isMinSm && (
                                <>
                                  <div styleName="markdown-wrapper">
                                    <StackedMarkdownPrice product={product} />
                                  </div>
                                  <div styleName="price-found-it-lower">
                                    <Price
                                      mapPrice={{
                                        mapPriceDisplay:
                                          mapDisplayRule?.mapPriceDisplay,
                                        mapRuleSatisfied,
                                        price: mapPrice?.value
                                      }}
                                      price={price}
                                      priceRange={priceRange}
                                      unitName={unitName}
                                      variant="H3"
                                    />
                                    <FoundItLowerButton
                                      arrowOrientation={
                                        FoundItLowerArrowOrientation.LEFT
                                      }
                                      hideIcon={!breakpoint.isMinLg}
                                      hideMatchItText={!breakpoint.isMinLg}
                                      productItem={{
                                        product: product,
                                        quantity: quantity,
                                        totalQuantity: totalQuantity
                                      }}
                                      theme={FoundItLowerTheme.BLUE}
                                    />
                                  </div>

                                  {showMinimumPriceMessage && (
                                    <MinimumPriceMessage
                                      message={mapDisplayRule?.message}
                                      styleName="standard-minimum-price-message"
                                    />
                                  )}

                                  {product.accessoryType ===
                                    ACCESSORY_TYPES.TPMS_SENSOR && (
                                    <TpmsAccessoryMessage styleName="tpms-accessory-message" />
                                  )}

                                  {isAccessory && unitOfMeasureDescription && (
                                    <div styleName="product-contains">
                                      <ProductContainsDescription
                                        product={product}
                                      />
                                    </div>
                                  )}
                                  {!isAccessory && (
                                    <div styleName="installation-costs">
                                      <InstallationCostsMessage />
                                    </div>
                                  )}
                                  <ParentProductMessage
                                    product={product}
                                    styleName="standard-parent-product-message"
                                  />
                                  <ProductPromotions
                                    brand={brand}
                                    productType={productType}
                                    promos={promos}
                                    showDuration
                                    showLongDescription
                                    showTooltip
                                  />
                                  {productType === PRODUCT_TYPES.WHEELS && (
                                    <div styleName="view-on-vehicle">
                                      <ViewOnVehicleButton
                                        hideFilters
                                        isCanonical={isCanonicalProduct}
                                        onClick={() =>
                                          trackAction(
                                            'product_detail_view_on_vehicle',
                                            getProductEntryDataLayerValues(
                                              product
                                            )
                                          )
                                        }
                                        product={
                                          product
                                            ? { ...product, swatches }
                                            : null
                                        }
                                        selectedSwatch={selectedSwatch}
                                        shouldSkipQuery
                                      />
                                    </div>
                                  )}
                                  {!isCanonicalSearch &&
                                    productType === PRODUCT_TYPES.WHEELS && (
                                      <ProductSwatchDescription
                                        finish={vendorFinish}
                                        styleName="wheel-swatch-description"
                                      />
                                    )}
                                </>
                              )}
                              {!isStaggered && !isSet && breakpoint.isMinSm && (
                                <>
                                  <div styleName="markdown-wrapper">
                                    <StackedMarkdownPrice product={product} />
                                  </div>
                                  <div styleName="price-found-it-lower">
                                    <Price
                                      mapPrice={{
                                        mapPriceDisplay:
                                          mapDisplayRule?.mapPriceDisplay,
                                        mapRuleSatisfied,
                                        price: mapPrice?.value
                                      }}
                                      price={price}
                                      priceRange={priceRange}
                                      unitName={unitName}
                                      variant="H3"
                                    />
                                    <FoundItLowerButton
                                      arrowOrientation={
                                        FoundItLowerArrowOrientation.LEFT
                                      }
                                      hideIcon={!breakpoint.isMinLg}
                                      hideMatchItText={!breakpoint.isMinLg}
                                      productItem={{
                                        product: product,
                                        quantity: quantity,
                                        totalQuantity: totalQuantity
                                      }}
                                      theme={FoundItLowerTheme.BLUE}
                                    />
                                  </div>
                                  {showMinimumPriceMessage && (
                                    <MinimumPriceMessage
                                      message={mapDisplayRule?.message}
                                      styleName="standard-minimum-price-message"
                                    />
                                  )}

                                  {product.accessoryType ===
                                    ACCESSORY_TYPES.TPMS_SENSOR && (
                                    <TpmsAccessoryMessage styleName="tpms-accessory-message" />
                                  )}

                                  {isAccessory && unitOfMeasureDescription && (
                                    <div styleName="product-contains">
                                      <ProductContainsDescription
                                        product={product}
                                      />
                                    </div>
                                  )}
                                  {!isAccessory && (
                                    <div styleName="installation-costs">
                                      <InstallationCostsMessage />
                                    </div>
                                  )}
                                  <ParentProductMessage
                                    product={product}
                                    styleName="standard-parent-product-message"
                                  />
                                  <ProductPromotions
                                    brand={brand}
                                    productType={productType}
                                    promos={promos}
                                    showDuration
                                    showLongDescription
                                    showTooltip
                                  />
                                  {productType === PRODUCT_TYPES.WHEELS && (
                                    <div styleName="view-on-vehicle">
                                      <ViewOnVehicleButton
                                        hideFilters
                                        isCanonical={isCanonicalProduct}
                                        onClick={() =>
                                          trackAction(
                                            'product_detail_view_on_vehicle',
                                            getProductEntryDataLayerValues(
                                              product
                                            )
                                          )
                                        }
                                        product={
                                          product
                                            ? { ...product, swatches }
                                            : null
                                        }
                                        selectedSwatch={selectedSwatch}
                                        shouldSkipQuery
                                      />
                                    </div>
                                  )}
                                </>
                              )}
                              {isSet && (
                                <>
                                  <div styleName="sets">
                                    {productSet?.map((product, idx) => {
                                      const { code, unitName } = product;

                                      const productSetPriceInfo =
                                        getPriceInfo(product);
                                      const productFitmentPosition =
                                        getProductFitmentPosition(product);
                                      const dashedProductFitmentPosition =
                                        productFitmentPosition
                                          ?.split(' ')
                                          ?.join('-');

                                      return (
                                        <>
                                          <div
                                            key={code}
                                            styleName="set-product"
                                          >
                                            <div styleName="set-product-checkbox-wrapper">
                                              <Checkbox
                                                isChecked={
                                                  productSetQuantity?.[idx] !==
                                                  0
                                                }
                                                onClick={e =>
                                                  updateQuantity(
                                                    e,
                                                    productSetQuantity?.[
                                                      idx
                                                    ] === 0
                                                      ? 1
                                                      : 0,
                                                    idx
                                                  )
                                                }
                                              />
                                            </div>
                                            <div styleName="set-product-content">
                                              <div styleName="set-product-content-details">
                                                <ProductSetImage
                                                  product={product}
                                                />
                                                <div>
                                                  <ProductSetDescription
                                                    product={product}
                                                    styleName="position-label"
                                                  />
                                                  <div styleName="set-price-found-it-lower">
                                                    <Price
                                                      mapPrice={
                                                        productSetPriceInfo?.mapPrice
                                                      }
                                                      price={
                                                        productSetPriceInfo?.price
                                                      }
                                                      priceRange={
                                                        productSetPriceInfo?.priceRange
                                                      }
                                                      unitName={unitName}
                                                      variant="H3"
                                                    />
                                                    <FoundItLowerButton
                                                      arrowOrientation={
                                                        FoundItLowerArrowOrientation.RIGHT
                                                      }
                                                      hideIcon={
                                                        !breakpoint.isMinLg
                                                      }
                                                      hideMatchItText={
                                                        !breakpoint.isMinLg
                                                      }
                                                      productItem={{
                                                        product: product,
                                                        quantity: quantity,
                                                        totalQuantity:
                                                          totalQuantity
                                                      }}
                                                      theme={
                                                        FoundItLowerTheme.BLUE
                                                      }
                                                    />
                                                    <ParentProductMessage
                                                      product={product}
                                                      styleName="parent-product-message"
                                                    />
                                                  </div>
                                                </div>
                                              </div>
                                              {productSetQuantity[idx] !==
                                                0 && (
                                                <ProductQuantity
                                                  aria-label={`${dashedProductFitmentPosition}-quantity-input`}
                                                  defaultValue={defaultQuantity}
                                                  id={`${dashedProductFitmentPosition}-quantity`}
                                                  max={99}
                                                  min={1}
                                                  onBlur={(e, val) =>
                                                    updateQuantity(e, val, idx)
                                                  }
                                                  onChange={(e, val) =>
                                                    updateQuantity(e, val, idx)
                                                  }
                                                  showLabel={
                                                    breakpoint.isMinSm
                                                      ? true
                                                      : false
                                                  }
                                                  value={
                                                    productSetQuantity[idx]
                                                  }
                                                />
                                              )}
                                            </div>
                                          </div>
                                          {!isSetSearch && idx === 0 && (
                                            <Typography styleName="frequently-bought-together">
                                              Frequently Bought Together
                                            </Typography>
                                          )}
                                        </>
                                      );
                                    })}
                                  </div>
                                  <ProductPromotions
                                    brand={brand}
                                    productType={productType}
                                    promos={promos}
                                    showDuration
                                    showLongDescription
                                    showTooltip
                                  />
                                </>
                              )}

                              {isStaggered && (
                                <div styleName="sets">
                                  <div styleName="front-axle">
                                    <div>
                                      <Typography
                                        styleName="axle-label"
                                        weight="black"
                                      >
                                        FRONT:
                                      </Typography>
                                      <ProductDescription
                                        code={code}
                                        size={frontSize}
                                      />
                                      <StackedMarkdownPrice product={product} />

                                      <div styleName="staggered-price-found-it-lower">
                                        <Price
                                          mapPrice={{
                                            mapPriceDisplay:
                                              mapDisplayRule?.mapPriceDisplay,
                                            mapRuleSatisfied,
                                            price: mapPrice?.value
                                          }}
                                          price={price}
                                          unitName={unitName}
                                          variant="H3"
                                        />
                                        <FoundItLowerButton
                                          arrowOrientation={
                                            FoundItLowerArrowOrientation.RIGHT
                                          }
                                          hideIcon={!breakpoint.isMinLg}
                                          hideMatchItText={!breakpoint.isMinLg}
                                          productItem={{
                                            product: product,
                                            quantity: quantity,
                                            totalQuantity: totalQuantity
                                          }}
                                          theme={FoundItLowerTheme.BLUE}
                                        />
                                      </div>

                                      {showMinimumPriceMessage && (
                                        <MinimumPriceMessage
                                          message={mapDisplayRule?.message}
                                        />
                                      )}
                                      <InstallationCostsMessage />
                                      <ParentProductMessage
                                        product={product}
                                        styleName="parent-product-message"
                                      />
                                    </div>
                                    <ProductQuantity
                                      aria-label="front-quantity-input"
                                      defaultValue={defaultQuantity}
                                      id="front-quantity"
                                      isHorizontal
                                      max={2}
                                      min={1}
                                      onBlur={updateQuantity}
                                      onChange={updateQuantity}
                                      showLabel={
                                        breakpoint.isMinSm ? true : false
                                      }
                                      value={quantity}
                                    />
                                  </div>
                                  <QuantityDisclaimer
                                    productType={productType}
                                    quantity={quantity}
                                    styleName="staggered-quantity-disclaimer-front"
                                  />

                                  <div styleName="rear-axle">
                                    <div>
                                      <Typography
                                        styleName="axle-label"
                                        weight="black"
                                      >
                                        REAR:
                                      </Typography>
                                      <ProductDescription
                                        code={rearCode}
                                        size={rearSize}
                                      />
                                      <StackedMarkdownPrice
                                        product={rearProduct}
                                      />
                                      <div styleName="staggered-price-found-it-lower">
                                        <Price
                                          mapPrice={{
                                            mapPriceDisplay:
                                              rearProduct?.mapDisplayRule
                                                ?.mapPriceDisplay,
                                            mapRuleSatisfied:
                                              rearProduct?.mapRuleSatisfied,
                                            price: rearProduct?.mapPrice?.value
                                          }}
                                          price={rearPrice}
                                          unitName={unitName}
                                          variant="H3"
                                        />
                                        <FoundItLowerButton
                                          arrowOrientation={
                                            FoundItLowerArrowOrientation.RIGHT
                                          }
                                          hideIcon={!breakpoint.isMinLg}
                                          hideMatchItText={!breakpoint.isMinLg}
                                          productItem={{
                                            product: rearProduct,
                                            quantity: rearQuantity,
                                            totalQuantity: totalQuantity
                                          }}
                                          theme={FoundItLowerTheme.BLUE}
                                        />
                                      </div>
                                      {showRearMinimumPriceMessage && (
                                        <MinimumPriceMessage
                                          message={
                                            rearProduct?.mapDisplayRule?.message
                                          }
                                        />
                                      )}
                                      <InstallationCostsMessage />
                                      <ParentProductMessage
                                        product={rearProduct}
                                        styleName="parent-product-message"
                                      />
                                    </div>
                                    <ProductQuantity
                                      aria-label="rear-quantity-input"
                                      defaultValue={defaultQuantity}
                                      id="rear-quantity"
                                      isHorizontal
                                      max={2}
                                      min={1}
                                      onBlur={updateRearQuantity}
                                      onChange={updateRearQuantity}
                                      showLabel={
                                        breakpoint.isMinSm ? true : false
                                      }
                                      value={rearQuantity}
                                    />
                                  </div>
                                  <QuantityDisclaimer
                                    productType={productType}
                                    quantity={rearQuantity}
                                    styleName="staggered-quantity-disclaimer-rear"
                                  />
                                  <ProductPromotions
                                    brand={brand}
                                    productType={productType}
                                    promos={promos}
                                    showDuration
                                    showLongDescription
                                    showTooltip
                                  />

                                  {productType === PRODUCT_TYPES.WHEELS && (
                                    <div
                                      styleName={`staggered-vov-swatch${
                                        isStaggered && isArrayWithLength(promos)
                                          ? ' staggered-vov-swatch--no-border'
                                          : ''
                                      }`}
                                    >
                                      <ViewOnVehicleButton
                                        hideFilters
                                        isCanonical={isCanonicalProduct}
                                        onClick={() =>
                                          trackAction(
                                            'product_detail_view_on_vehicle',
                                            getProductEntryDataLayerValues(
                                              product
                                            )
                                          )
                                        }
                                        product={
                                          product
                                            ? { ...product, swatches }
                                            : null
                                        }
                                        selectedSwatch={selectedSwatch}
                                        shouldSkipQuery
                                      />
                                      <ProductSwatchDescription
                                        finish={vendorFinish}
                                        styleName="staggered-wheel-swatch-description"
                                      />
                                    </div>
                                  )}
                                </div>
                              )}
                              {!breakpoint.isMinSm && icons.length != 0 && (
                                <div
                                  id="tire-attribute-icons"
                                  styleName="tire-attribute-icons"
                                >
                                  <PdpFeatureIcons
                                    hideWarranty={treadLifeMedian}
                                    icons={icons}
                                    rollingResistanceRating={
                                      recommendationData?.stopping
                                        ?.rollingResistanceRating
                                    }
                                    warranty={getProductWarranty(product)}
                                  />
                                </div>
                              )}
                              {(isOtherVehiclesOE ||
                                product?.['isOtherVehiclesOE']) &&
                                selectedFitment !== null && (
                                  <ProductOECompatible
                                    styleName={`product-oe-compatible ${
                                      isCanonicalSearch
                                        ? 'product-oe-compatible--is-canonical'
                                        : ''
                                    }`}
                                  />
                                )}

                              {!isCanonicalSearch &&
                                !isStaggered &&
                                !isSet &&
                                productType === PRODUCT_TYPES.WHEELS &&
                                breakpoint.isMinSm && (
                                  <ProductSwatchDescription
                                    finish={vendorFinish}
                                    styleName="wheel-swatch-description"
                                  />
                                )}
                              {isCanonicalSearch && product && (
                                <div styleName="form-container">
                                  {isLoadingProduct && (
                                    <div styleName="loading-overlay">
                                      <LoadingSpinner />
                                    </div>
                                  )}
                                  {pageType === PRODUCT_TYPES.ACCESSORIES &&
                                    !isRefreshingData && (
                                      <LazyLoad>
                                        <CanonicalAccessoryForm
                                          isLoading={isLoadingProduct}
                                          onFilterChange={onFilterChange}
                                          onSelectSwatch={saveSelectedSwatch}
                                          product={product}
                                          selectedSwatch={selectedSwatch}
                                          swatches={swatches}
                                        />
                                      </LazyLoad>
                                    )}

                                  {pageType === PRODUCT_TYPES.TIRES &&
                                    !isRefreshingData && (
                                      <LazyLoad>
                                        <CanonicalTireForm
                                          isLoading={isLoadingProduct}
                                          onFilterChange={onFilterChange}
                                          product={product}
                                        />
                                      </LazyLoad>
                                    )}

                                  {pageType === PRODUCT_TYPES.WHEELS &&
                                    !isRefreshingData && (
                                      <LazyLoad>
                                        <CanonicalWheelForm
                                          isLoading={isLoadingProduct}
                                          onFilterChange={onFilterChange}
                                          onSelectSwatch={saveSelectedSwatch}
                                          product={product}
                                          selectedSwatch={selectedSwatch}
                                          swatches={swatches}
                                        />
                                      </LazyLoad>
                                    )}
                                </div>
                              )}

                              {shouldShowProductArticleStatusBanner ? (
                                <ProductArticleStatusBanner
                                  product={articleStatusProduct}
                                  styleName={
                                    articleStatusProduct?.articleStatus ===
                                    ARTICLE_STATUSES.QUALITY_HOLD
                                      ? ''
                                      : 'coming-soon-banner'
                                  }
                                />
                              ) : (
                                <>
                                  {!isCanonicalProduct &&
                                    shouldShowProductAvailability && (
                                      <StoreAvailabilityOptions
                                        myStoreCode={myStore?.code}
                                        onClickStore={setSelectedStoreCode}
                                        prebuiltPackage={prebuiltPackage}
                                        product={articleStatusProduct}
                                        quantity={quantity}
                                        rearProduct={rearProduct}
                                        rearQuantity={rearQuantity}
                                        selectedStoreCode={selectedStoreCode}
                                      />
                                    )}
                                </>
                              )}

                              {!breakpoint.isMinSm &&
                                showTreadwellProductExplanation && (
                                  <TreadwellProductExplanation
                                    compareSection={
                                      showExplanationCompareSection && (
                                        <div styleName="treadwell-product-explanation-comparison-container">
                                          <Typography>
                                            See how this tire compares to the #1
                                            Treadwell recommendation for your
                                            car, your driving area, and your
                                            driving style
                                          </Typography>
                                          <Button
                                            isTertiary
                                            onClick={handleProductComparison}
                                          >
                                            See tire comparison
                                          </Button>
                                        </div>
                                      )
                                    }
                                    pageType={PageTypes.PDP}
                                    products={explanationProducts}
                                    requestType={explanationRequestType}
                                    styleName="mobile-treadwell-product-explanation"
                                  />
                                )}

                              {articleStatusProduct?.articleStatus ===
                                ARTICLE_STATUSES.QUALITY_HOLD && (
                                <p styleName="quality-hold-msg">
                                  Call store for info:{' '}
                                  <PhoneNumber
                                    isUnformatted
                                    phone={myStore?.address?.phone}
                                  />
                                </p>
                              )}

                              {isCanonicalSearch &&
                                !selectedFitment &&
                                isCanonicalProduct && (
                                  <Typography
                                    styleName="price-message"
                                    tag="div"
                                  >
                                    <Button
                                      href="#/fitment/vehicle"
                                      onClick={() =>
                                        trackAction(
                                          'product_fitment_validation_product'
                                        )
                                      }
                                      size="regular"
                                    >
                                      Enter vehicle
                                    </Button>{' '}
                                    for exact price and inventory
                                  </Typography>
                                )}
                              {isArrayWithLength(filteredServices) && (
                                <div styleName="store-featured-services">
                                  {filteredServices?.map((service, index) => {
                                    const serviceCode = service!.code as string;

                                    return (
                                      <div key={index} styleName="service-item">
                                        <div styleName="icon-container">
                                          {
                                            FEATURED_SERVICES_FOR_PDP[
                                              serviceCode
                                            ].icon
                                          }
                                        </div>
                                        <Typography styleName="italic-text">
                                          {
                                            FEATURED_SERVICES_FOR_PDP[
                                              serviceCode
                                            ].linkDescriptionStart
                                          }{' '}
                                          {service?.link ? (
                                            <Link to={service.link}>
                                              {
                                                FEATURED_SERVICES_FOR_PDP[
                                                  serviceCode
                                                ].linkText
                                              }
                                            </Link>
                                          ) : (
                                            <>
                                              {
                                                FEATURED_SERVICES_FOR_PDP[
                                                  serviceCode
                                                ].linkText
                                              }
                                            </>
                                          )}
                                          {
                                            FEATURED_SERVICES_FOR_PDP[
                                              serviceCode
                                            ].linkDescriptionEnd
                                          }
                                        </Typography>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}

                              {pageType === PRODUCT_TYPES.TIRES &&
                                breakpoint.isMinSm && (
                                  <div styleName="reviews-promos">
                                    <Typography
                                      styleName="ratings-header"
                                      tag="span"
                                      variant="H6"
                                      weight="black"
                                    >
                                      Ratings
                                    </Typography>
                                    <div styleName="reviews-promos-group">
                                      <StarRating
                                        rating={productRating}
                                        styleName="reviews-star-rating"
                                      />
                                      <RatingLink
                                        count={productReviewCount}
                                        onClick={onReadReviewsClick}
                                        styleName="reviews-link"
                                      />
                                    </div>
                                  </div>
                                )}
                            </>
                          )}

                          {isNoLongerAvailable && reviewSummary && (
                            <div styleName="parent-product-reviews">
                              <StarRating rating={productRating} />
                              <RatingLink
                                count={productReviewCount}
                                onClick={onReadReviewsClick}
                              />
                            </div>
                          )}

                          {!isNoLongerAvailable && (
                            <>
                              {pageType === PRODUCT_TYPES.TIRES &&
                                (recommendationData ||
                                  hasTdsStoppingData ||
                                  hasTdsWinterData) && (
                                  <ProductFirstPriorityDetails
                                    dataReportUrl={url}
                                    onClick={() => {
                                      trackAction(
                                        'product_detail_tire_test_data_view'
                                      );
                                      scrollToTreadwell();
                                    }}
                                    product={product}
                                  />
                                )}
                              {breakpoint.isMinSm && icons.length != 0 && (
                                <div
                                  id="tire-attribute-icons"
                                  styleName="tire-attribute-icons"
                                >
                                  <PdpFeatureIcons
                                    hideWarranty={treadLifeMedian}
                                    icons={icons}
                                    rollingResistanceRating={
                                      recommendationData?.stopping
                                        ?.rollingResistanceRating
                                    }
                                    warranty={getProductWarranty(product)}
                                  />
                                </div>
                              )}

                              <PdpDealsSection
                                combinedMapPrice={combinedMapPrice}
                                isAddToCartDisabled={!showAddToCartComponents}
                                isCanonicalProduct={isCanonicalProduct}
                                promos={promos}
                                topRecommendedProductData={
                                  featureFlags?.[
                                    FEATURE_FLAGS.__FF_COST_TO_SERVE_IN_STOCK__
                                  ] &&
                                  featureFlags?.[
                                    FEATURE_FLAGS
                                      .__FF_COST_TO_SERVE_IN_STOCK_TREADWELL__
                                  ]
                                    ? productRecommendationData?.topRecommendedProductData
                                    : undefined
                                }
                              />

                              {pageType === PRODUCT_TYPES.TIRES &&
                                isArrayWithLength(services) && (
                                  <PDPFreeServices
                                    heading={servicesHeading}
                                    services={services}
                                  />
                                )}
                              <MyAccountSavingsAppliedCartMessage
                                potentialPromotions={promos}
                              />
                              {!showAddToCartComponents &&
                                articleStatusProduct?.articleStatus ===
                                  ARTICLE_STATUSES.DISCONTINUED &&
                                !isPrebuiltPackage && (
                                  <ShopReplacementButton
                                    product={product}
                                    productType={productType}
                                  />
                                )}

                              {showAddToCartComponents &&
                              !shouldShowPdpStickyAddToCart ? (
                                <PdpAddToCartSection
                                  defaultQuantity={defaultQuantity}
                                  isCanonicalProduct={isCanonicalProduct}
                                  isDisabled={isUnsupportedCommercialProduct}
                                  isSet={isSet}
                                  isStaggered={isStaggered}
                                  myStore={myStore}
                                  productItem={
                                    isSet
                                      ? productSetItem
                                      : {
                                          prebuiltPackage,
                                          product,
                                          quantity,
                                          rearProduct,
                                          rearQuantity,
                                          totalQuantity
                                        }
                                  }
                                  productType={productType}
                                  storeCode={selectedStoreCode}
                                  updateQuantity={updateQuantity}
                                />
                              ) : (
                                <>
                                  {process.env
                                    .__FF_UPDATE_PDP_STICKY_FOOTER__ ===
                                  'true' ? (
                                    <StickyAddToCartSectionOneDt
                                      defaultQuantity={defaultQuantity}
                                      isCanonicalProduct={isCanonicalProduct}
                                      isDisabled={
                                        isUnsupportedCommercialProduct
                                      }
                                      isSet={isSet}
                                      isStaggered={isStaggered}
                                      price={price}
                                      productItem={
                                        isSet
                                          ? productSetItem
                                          : {
                                              prebuiltPackage,
                                              product,
                                              quantity,
                                              rearProduct,
                                              rearQuantity,
                                              totalQuantity
                                            }
                                      }
                                      productType={productType}
                                      selectedFitment={selectedFitment}
                                      storeCode={selectedStoreCode}
                                      updateQuantity={updateQuantity}
                                    />
                                  ) : (
                                    <StickyAddToCartSection
                                      defaultQuantity={defaultQuantity}
                                      isCanonicalProduct={isCanonicalProduct}
                                      isDisabled={
                                        isUnsupportedCommercialProduct
                                      }
                                      isSet={isSet}
                                      isStaggered={isStaggered}
                                      price={price}
                                      productItem={
                                        isSet
                                          ? productSetItem
                                          : {
                                              prebuiltPackage,
                                              product,
                                              quantity,
                                              rearProduct,
                                              rearQuantity,
                                              totalQuantity
                                            }
                                      }
                                      productType={productType}
                                      selectedFitment={selectedFitment}
                                      storeCode={selectedStoreCode}
                                      updateQuantity={updateQuantity}
                                    />
                                  )}
                                </>
                              )}
                            </>
                          )}
                        </>
                      )}
                      {isNoLongerAvailable && (
                        <>
                          {productType === PRODUCT_TYPES.WHEELS && (
                            <ProductSwatchDescription
                              finish={getWheelFinish(product)}
                            />
                          )}
                          {product?.articleStatusLabel && (
                            <div styleName="obsolete-message">
                              <ExclamationTriangleSolidIcon styleName="icon" />
                              <span>{product.articleStatusLabel}</span>
                            </div>
                          )}
                          {!replacementProduct ? (
                            <ShopReplacementButton
                              product={product}
                              productType={productType}
                            />
                          ) : (
                            <div styleName="replaced-with">
                              <div styleName="replacement-product-description">
                                <ProductImage
                                  height={125}
                                  product={replacementProduct}
                                  width={125}
                                />
                                <div>
                                  <ProductDescription
                                    brand={replacementProduct?.brand}
                                    brandLogoUrl={
                                      replacementProduct?.brandLogo?.url
                                    }
                                    code={replacementProduct?.code}
                                    name={replacementProduct?.name}
                                    size={replacementProduct?.size}
                                  />
                                  <TireAttributeIcons
                                    icons={getTireAttributeIcons(
                                      replacementProduct
                                    )}
                                  />
                                  <Price
                                    mapPrice={{
                                      mapPriceDisplay:
                                        replacementProduct?.mapDisplayRule
                                          ?.mapPriceDisplay,
                                      mapRuleSatisfied:
                                        replacementProduct?.mapRuleSatisfied,
                                      price: replacementProduct?.mapPrice?.value
                                    }}
                                    price={replacementProduct?.price?.value}
                                    priceRange={replacementProduct?.priceRange}
                                    unitName={replacementProduct?.unitName}
                                    variant="H3"
                                  />

                                  <FoundItLowerButton
                                    analyticsPrefix="product_nla_replacement"
                                    arrowOrientation={
                                      FoundItLowerArrowOrientation.LEFT
                                    }
                                    hideIcon={!breakpoint.isMinLg}
                                    hideMatchItText={!breakpoint.isMinLg}
                                    productItem={{
                                      product: replacementProduct,
                                      quantity: quantity,
                                      totalQuantity: totalQuantity
                                    }}
                                    theme={FoundItLowerTheme.BLUE}
                                  />
                                  <ParentProductMessage
                                    product={replacementProduct}
                                    styleName="parent-product-message"
                                  />
                                </div>
                              </div>
                              {Boolean(
                                replacementProductPromos ||
                                  replacementProduct?.reviewSummaryData
                              ) && (
                                <div styleName="replacement-product-row">
                                  {Boolean(
                                    replacementProduct?.reviewSummaryData
                                  ) && (
                                    <div>
                                      <StarRating
                                        rating={
                                          replacementProduct?.reviewSummaryData
                                            ?.totalRating || 0
                                        }
                                      />
                                      {Boolean(replacementProduct?.url) && (
                                        <RatingLink
                                          count={
                                            replacementProduct
                                              ?.reviewSummaryData?.totalCount
                                          }
                                          href={`${replacementProduct?.url}#${CUSTOMER_REVIEWS_ELEMENT_ID}`}
                                          onClick={() =>
                                            trackAction(
                                              'product_nla_replacement_product_reviews_click',
                                              getProductEntryDataLayerValues(
                                                replacementProduct
                                              )
                                            )
                                          }
                                        />
                                      )}
                                    </div>
                                  )}
                                  {Boolean(replacementProductPromos) && (
                                    <div>
                                      <ProductPromotions
                                        promos={replacementProductPromos}
                                      />
                                    </div>
                                  )}
                                </div>
                              )}

                              {Boolean(
                                replacementProduct?.stock?.availabilityMessage
                              ) && (
                                <div styleName="replacement-product-row">
                                  <div>
                                    <strong>
                                      <ProductAvailabilityMessage
                                        availabilityMessage={
                                          replacementProduct?.stock
                                            ?.availabilityMessage
                                        }
                                        hideIcons
                                      />
                                      <MyStoreMarker showAtPrefix />
                                    </strong>
                                  </div>
                                </div>
                              )}

                              <Button
                                href={replacementProduct?.url}
                                isPrimary
                                onClick={() =>
                                  trackAction(
                                    'product_nla_replacement_product_click',
                                    getProductEntryDataLayerValues(
                                      replacementProduct
                                    )
                                  )
                                }
                                styleName="view-product-details"
                              >
                                View Product Details
                              </Button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </Section>

          {prebuiltPackage && (
            <>
              <Tabs styleName="product-detail-tabs">
                <Tab
                  isSelected={selectedTabIndex === 1}
                  onClick={() => setSelectedTabIndex(1)}
                >
                  Tire Specs
                </Tab>
                <Tab
                  isSelected={selectedTabIndex === 2}
                  onClick={() => setSelectedTabIndex(2)}
                >
                  Wheel Specs
                </Tab>
              </Tabs>
              <div role="tabpanel" styleName="product-detail-tab-content">
                <div styleName="product-overview-header">
                  Product Overview
                  <br />
                  {selectedPrebuiltPackageProduct?.brand}{' '}
                  {selectedPrebuiltPackageProduct?.name}
                </div>
                {!window.isPreRendering && (
                  <ProductDetails
                    hideSizeTable={isPrebuiltPackage}
                    isCanonicalSearch={isCanonicalSearch}
                    key={selectedTabIndex}
                    onSizeSelected={
                      process.env.__FF_PDP_TIRE_SIZE_SEO_LINKS__ === 'true' ||
                      process.env.__FF_PDP_WHEEL_SIZE_SEO_LINKS__ === 'true'
                        ? undefined
                        : onFilterChange
                    }
                    product={selectedPrebuiltPackageProduct}
                    productType={getNormalizedProductType(
                      selectedPrebuiltPackageProduct
                    )}
                    queryFilters={queryFilters}
                    selectedFitment={selectedFitment}
                    storeCode={storeCode}
                    styleName="prebuilt-product-details"
                    tooltips={tooltips}
                  />
                )}
              </div>
            </>
          )}

          {recommendationData && !isPrebuiltPackage && (
            <Section id={TREADWELL_DATA_ELEMENT_ID} isFullWidth showDivider>
              <LazyLoad
                placeholder={
                  <div className="treadwell-data-placeholder">Loading...</div>
                }
              >
                <div styleName="treadwell">
                  {weatherDetails && (
                    <PdpTreadwellSection
                      product={
                        product ||
                        getPrebuiltPackageProduct(PRODUCT_TYPES.TIRES)
                      }
                      weatherDetails={weatherDetails}
                    />
                  )}
                </div>
              </LazyLoad>
            </Section>
          )}

          {product && !isPrebuiltPackage && (
            <Section showDivider>
              <div styleName="product-info">
                <h2>product info</h2>
                {!window.isPreRendering && (
                  <h1>{overviewHeading || `${brand} ${name}`}</h1>
                )}
                <hr />
              </div>
              {!window.isPreRendering && !isLoadingProduct && (
                <LazyLoad
                  placeholder={
                    <div className="product-info-placeholder">Loading...</div>
                  }
                >
                  <ProductDetails
                    isCanonicalSearch={isCanonicalSearch}
                    onSizeSelected={
                      process.env.__FF_PDP_TIRE_SIZE_SEO_LINKS__ === 'true' ||
                      process.env.__FF_PDP_WHEEL_SIZE_SEO_LINKS__ === 'true'
                        ? undefined
                        : onFilterChange
                    }
                    product={product}
                    productType={pageType}
                    queryFilters={queryFilters}
                    selectedFitment={selectedFitment}
                    shouldForceRenderLazyChildren={shouldScrollToReviews}
                    storeCode={storeCode}
                    tooltips={tooltips}
                  />
                </LazyLoad>
              )}
            </Section>
          )}

          {!isLoadingProduct &&
            !window.isPreRendering &&
            pageType === PRODUCT_TYPES.TIRES && (
              <Section heading="Customer Reviews" id="read-reviews">
                <LazyLoad
                  placeholder={
                    <div className="read-reviews-placeholder">Loading...</div>
                  }
                >
                  <ProductReviews
                    averageRatings={averageRatings}
                    hasProductLineReviews={hasProductLineReviews}
                    includeVariantProductReviews={includeVariantProductReviews}
                    productReviews={productReviews}
                    reviewSummary={reviewSummary}
                    reviews={reviews}
                    setIncludeVariantProductReviews={
                      setIncludeVariantProductReviews
                    }
                    setPageNumber={setPageNumber}
                    setReviewsFilterSortQuery={setReviewsFilterSortQuery}
                  />
                </LazyLoad>
              </Section>
            )}

          {suggestedSellingPageContext &&
            selectedFitment &&
            product &&
            !currentTireWheelPackageFlow && (
              <Suspense fallback={<div />}>
                <SuggestedSellingPageSection
                  axleType={suggestedSellingAxleType}
                  baseProduct={product}
                  pageContext={suggestedSellingPageContext}
                  prevPageUrl={prevPageUrl}
                />
              </Suspense>
            )}
        </Content>
      </Page>
    </DefaultLayout>
  );
}

export default hot(ProductDetailPage);
